/**
 * SettingsContext — visibilidad de la UI configurada por el admin.
 *
 * El admin decide desde su panel qué ve el cliente (menú, marketplace, App Deploy,
 * bases de datos, máquinas virtuales). Esa config vive en DynamoDB (GlobalSettings)
 * y se sirve pública en GET /products/ui-settings (misma lambda que /products).
 *
 * Semántica CLAVE: `missing = visible`. Una superficie se OCULTA solo si su
 * bandera está explícitamente en `false`. Si la config no cargó, falta una clave,
 * o el endpoint falla, se MUESTRA todo (retrocompatible). Por eso los helpers
 * regresan `true` por defecto.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

// Estructura de secciones (espejo del backend admin settings.py).
export interface UISettings {
  menu?: { items?: Record<string, boolean> };
  marketplace?: { enabled?: boolean; products?: Record<string, boolean> };
  appDeploy?: {
    enabled?: boolean;
    static?: boolean;
    server?: boolean;
    staticFrameworks?: Record<string, boolean>;
    serverRuntimes?: Record<string, boolean>;
  };
  database?: { enabled?: boolean; engines?: Record<string, boolean> };
  // vm.products: mapa por id de producto VM del catálogo (missing = visible).
  vm?: { enabled?: boolean; products?: Record<string, boolean> };
}

interface SettingsContextValue {
  settings: UISettings;
  loaded: boolean;
  refresh: () => void;
  // Menú principal (claves = navigationConfig): overview, infrastructure,
  // deployApps, virtualMachines, databases, chatbotIA, marketplace, orders.
  isMenuItemVisible: (key: string) => boolean;
  // Áreas
  isMarketplaceEnabled: () => boolean;
  isMarketplaceProductVisible: (key: string) => boolean;
  isAppDeployEnabled: () => boolean;
  isAppDeployStaticEnabled: () => boolean;
  isAppDeployServerEnabled: () => boolean;
  isStaticFrameworkVisible: (id: string) => boolean;
  isServerRuntimeVisible: (id: string) => boolean;
  isDatabaseEnabled: () => boolean;
  isDbEngineVisible: (id: string) => boolean;
  isVmEnabled: () => boolean;
  isVmProductVisible: (id: string | undefined | null) => boolean;
}

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

/** Traversa un path de banderas; devuelve `def` si falta o no es booleano. */
function flag(obj: any, path: string[], def = true): boolean {
  let cur = obj;
  for (const k of path) {
    if (cur == null || typeof cur !== 'object' || !(k in cur)) return def;
    cur = cur[k];
  }
  return typeof cur === 'boolean' ? cur : def;
}

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accessToken } = useAuth();
  const [settings, setSettings] = useState<UISettings>({});
  const [loaded, setLoaded] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      // La lambda devuelve { ok, settings, updatedAt, version }.
      const res = await apiService.get<any>('/products/ui-settings');
      setSettings(res?.settings || {});
    } catch (error) {
      // Silencioso a propósito: sin config, se muestra todo.
      console.warn('[settings] no se pudo cargar la config de UI; se muestra todo.', error);
      setSettings({});
    } finally {
      setLoaded(true);
    }
  }, []);

  // Carga al montar y cuando aparece el token (el endpoint puede requerir auth).
  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const value = useMemo<SettingsContextValue>(() => {
    // El menú de un área también respeta el master switch del área.
    const areaEnabledForMenu: Record<string, string[]> = {
      deployApps: ['appDeploy', 'enabled'],
      virtualMachines: ['vm', 'enabled'],
      databases: ['database', 'enabled'],
      marketplace: ['marketplace', 'enabled'],
    };

    return {
      settings,
      loaded,
      refresh: fetchSettings,
      isMenuItemVisible: (key: string) => {
        if (!flag(settings, ['menu', 'items', key])) return false;
        const areaPath = areaEnabledForMenu[key];
        return areaPath ? flag(settings, areaPath) : true;
      },
      isMarketplaceEnabled: () => flag(settings, ['marketplace', 'enabled']),
      isMarketplaceProductVisible: (key: string) =>
        flag(settings, ['marketplace', 'enabled']) &&
        flag(settings, ['marketplace', 'products', key]),
      isAppDeployEnabled: () => flag(settings, ['appDeploy', 'enabled']),
      isAppDeployStaticEnabled: () =>
        flag(settings, ['appDeploy', 'enabled']) && flag(settings, ['appDeploy', 'static']),
      isAppDeployServerEnabled: () =>
        flag(settings, ['appDeploy', 'enabled']) && flag(settings, ['appDeploy', 'server']),
      isStaticFrameworkVisible: (id: string) => flag(settings, ['appDeploy', 'staticFrameworks', id]),
      isServerRuntimeVisible: (id: string) => flag(settings, ['appDeploy', 'serverRuntimes', id]),
      isDatabaseEnabled: () => flag(settings, ['database', 'enabled']),
      isDbEngineVisible: (id: string) =>
        flag(settings, ['database', 'enabled']) && flag(settings, ['database', 'engines', id]),
      isVmEnabled: () => flag(settings, ['vm', 'enabled']),
      // Por id de producto VM del catálogo; missing = visible (producto sin
      // bandera o sin id => visible).
      isVmProductVisible: (id) => {
        if (!id) return true;
        return flag(settings, ['vm', 'products', id]);
      },
    };
  }, [settings, loaded, fetchSettings]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettings = (): SettingsContextValue => {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error('useSettings debe usarse dentro de <SettingsProvider>');
  }
  return ctx;
};
